import React, { useMemo, useRef, useEffect, useCallback } from "react";
import { formatTimeInTZ, formatDateInTZ } from "../../lib/timezone";

// =============================================================================
// REPLY TIMELINE — Conversation metadata sidebar
// =============================================================================
// PURPOSE: Compressed timeline showing who replied when and how fast.
// Sits alongside the message view in ConversationViewModal.
//
// FEATURES:
// - Reply turn annotations with speed color coding
// - Day separators (timezone-aware)
// - Stats header (total turns, avg reply speed)
// - Click-to-scroll: clicking a timeline entry scrolls the messages panel
// - Synchronized scrolling: scrolling messages highlights the current entry
//
// DATA: Computed entirely from the messages array — no new RPCs.
// =============================================================================

interface Message {
  id: string;
  message_text: string | null;
  sent_at: string;
  sender_type: "lead" | "setter" | string;
  has_media?: boolean;
  ghl_sender_user_id?: string | null;
}

interface TimelineEntry {
  messageId: string;
  sender: "lead" | "setter";
  time: string; // UTC ISO
  displayName: string;
  /** Minutes since previous sender's first message (only on sender transitions) */
  replyMinutes: number | null;
  /** Is this a reply turn (setter responding after lead message)? */
  isReplyTurn: boolean;
  /** Day key for separator detection (timezone-aware) */
  dayKey: string;
  /** Is this within the active date range? */
  dimmed: boolean;
}

interface TimelineStats {
  totalTurns: number;
  turnsInRange: number;
  avgReplyMins: number | null;
  medianReplyMins: number | null;
  totalMessages: number;
  leadMessages: number;
  setterMessages: number;
}

interface ReplyTimelineProps {
  messages: Message[];
  timezone: string;
  setterName: string | null;
  contactName: string;
  activeDateRange?: { start: Date; end: Date } | null;
  /** Callback when a timeline entry is clicked — scrolls the messages panel */
  onEntryClick: (messageId: string) => void;
  /** Currently visible message ID from the messages panel (for sync highlight) */
  activeMessageId?: string | null;
  /** Lifetime reply turns from ghl_conversations.reply_turns */
  lifetimeReplyTurns?: number | null;
}

// Is this a ManyChat automation message?
function isManyChat(msg: Message): boolean {
  if (msg.sender_type !== "setter") return false;
  // If ghl_sender_user_id is available, use it (most reliable)
  if ("ghl_sender_user_id" in msg) {
    return !msg.ghl_sender_user_id;
  }
  // Fallback: check message text patterns
  return !msg.message_text || msg.message_text.trim() === "";
}

function computeTimeline(
  messages: Message[],
  timezone: string,
  setterName: string | null,
  contactName: string,
  activeDateRange?: { start: Date; end: Date } | null
): { entries: TimelineEntry[]; stats: TimelineStats } {
  const entries: TimelineEntry[] = [];
  const replyTimes: number[] = [];
  let totalTurns = 0;
  let turnsInRange = 0;
  let leadMessages = 0;
  let setterMessages = 0;

  // Track the first unanswered lead message timestamp
  let firstUnansweredLeadAt: Date | null = null;
  let lastSender: string | null = null;

  for (const msg of messages) {
    // Skip ManyChat automation from timeline
    if (isManyChat(msg)) continue;

    const sender = msg.sender_type === "lead" ? "lead" : "setter";
    if (sender === "lead") leadMessages++;
    else setterMessages++;

    const msgTime = new Date(msg.sent_at);
    const dayKey = formatDateInTZ(msg.sent_at, timezone, { short: true });

    // Check if within active date range
    let dimmed = false;
    if (activeDateRange) {
      dimmed =
        msgTime.getTime() < activeDateRange.start.getTime() ||
        msgTime.getTime() > activeDateRange.end.getTime();
    }

    let replyMinutes: number | null = null;
    let isReplyTurn = false;

    if (sender === "lead" && lastSender !== "lead") {
      // First lead message after setter (or start) — start the reply timer
      firstUnansweredLeadAt = msgTime;
    }

    if (sender === "setter" && firstUnansweredLeadAt && lastSender !== "setter") {
      // First setter message after lead message(s) = reply turn
      isReplyTurn = true;
      totalTurns++;
      if (!dimmed) turnsInRange++;
      replyMinutes = (msgTime.getTime() - firstUnansweredLeadAt.getTime()) / 60000;
      replyTimes.push(replyMinutes);
      firstUnansweredLeadAt = null;
    }

    entries.push({
      messageId: msg.id,
      sender,
      time: msg.sent_at,
      displayName: sender === "lead" ? contactName : setterName || "Setter",
      replyMinutes,
      isReplyTurn,
      dayKey,
      dimmed,
    });

    lastSender = sender;
  }

  // Compute stats
  const sorted = [...replyTimes].sort((a, b) => a - b);
  const avgReplyMins = sorted.length > 0 ? sorted.reduce((a, b) => a + b, 0) / sorted.length : null;
  const medianReplyMins =
    sorted.length > 0
      ? sorted.length % 2 === 0
        ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
        : sorted[Math.floor(sorted.length / 2)]
      : null;

  return {
    entries,
    stats: {
      totalTurns,
      turnsInRange,
      avgReplyMins,
      medianReplyMins,
      totalMessages: leadMessages + setterMessages,
      leadMessages,
      setterMessages,
    },
  };
}

function formatDuration(mins: number): string {
  if (mins < 1) return "<1 min";
  if (mins < 60) return `${Math.round(mins)} min`;
  const hours = Math.floor(mins / 60);
  const remainMins = Math.round(mins % 60);
  if (hours < 24) return remainMins > 0 ? `${hours}h ${remainMins}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const remainHours = hours % 24;
  return remainHours > 0 ? `${days}d ${remainHours}h` : `${days}d`;
}

function replySpeedColor(mins: number): string {
  if (mins < 5) return "text-ds-green";
  if (mins < 30) return "text-ds-amber";
  return "text-ds-red";
}

export const ReplyTimeline: React.FC<ReplyTimelineProps> = ({
  messages,
  timezone,
  setterName,
  contactName,
  activeDateRange,
  onEntryClick,
  activeMessageId,
  lifetimeReplyTurns,
}) => {
  const { entries, stats } = useMemo(
    () => computeTimeline(messages, timezone, setterName, contactName, activeDateRange),
    [messages, timezone, setterName, contactName, activeDateRange]
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const entryRefs = useRef<Map<string, HTMLElement>>(new Map());

  const setEntryRef = useCallback((id: string, el: HTMLElement | null) => {
    if (el) entryRefs.current.set(id, el);
    else entryRefs.current.delete(id);
  }, []);

  // Auto-scroll timeline to keep active message visible
  useEffect(() => {
    if (!activeMessageId) return;
    const el = entryRefs.current.get(activeMessageId);
    if (el && containerRef.current) {
      const container = containerRef.current;
      const elRect = el.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      // Only scroll if the element is outside the visible area
      if (elRect.top < containerRect.top || elRect.bottom > containerRect.bottom) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [activeMessageId]);

  // Track previous day for separator rendering
  // prevDay tracking removed — use index-based comparison below

  return (
    <div className="flex flex-col h-full min-w-0">
      {/* Stats Header */}
      <div className="px-3 py-2 border-b border-black/[0.08] flex-shrink-0">
        <div className="flex items-baseline justify-between mb-1">
          <span className="token-13m font-mono text-glass">
            {lifetimeReplyTurns ?? stats.totalTurns} turns
          </span>
          {stats.turnsInRange !== stats.totalTurns && (
            <span className="token-11 text-glass-muted">{stats.turnsInRange} in range</span>
          )}
        </div>
        <div className="flex gap-3 token-11 text-glass-muted">
          {/* These two read "avg" and "med" with NO noun, which is the whole reason
              anyone files a bug against a correct build: a viewer supplies the missing
              noun from the panel title, reads it as "reply time", and compares it to the
              scorecard. It is not that number. The noun is `wait` (Mako, Aug 6 2026) and
              it is raw wall-clock, so it reads HIGHER than Setter Reply Time by design.
              Add the noun to the OUTER span only. The inner spans keep the bare duration
              as their textContent, which is what the shipped tests read. */}
          {stats.avgReplyMins !== null && (
            <span>
              avg wait{" "}
              <span
                data-testid="reply-timeline-avg"
                className={replySpeedColor(stats.avgReplyMins)}
              >
                {formatDuration(stats.avgReplyMins)}
              </span>
            </span>
          )}
          {stats.medianReplyMins !== null && (
            <span>
              med wait{" "}
              <span
                data-testid="reply-timeline-med"
                className={replySpeedColor(stats.medianReplyMins)}
              >
                {formatDuration(stats.medianReplyMins)}
              </span>
            </span>
          )}
        </div>
        <div className="flex gap-3 token-11 text-glass-muted mt-0.5">
          <span>{stats.leadMessages} lead</span>
          <span>{stats.setterMessages} setter</span>
        </div>
      </div>

      {/* Timeline entries */}
      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto px-3 py-2"
        style={{ overscrollBehavior: "contain" }}
      >
        {entries.length === 0 && (
          <p className="token-11 text-glass-muted text-center py-4">No messages</p>
        )}

        {entries.map((entry, idx) => {
          // Day separator
          const showDaySep = idx === 0 || entry.dayKey !== entries[idx - 1].dayKey;

          const isActive = entry.messageId === activeMessageId;

          return (
            <React.Fragment key={entry.messageId}>
              {showDaySep && (
                <div className="flex items-center gap-2 my-2">
                  <div className="flex-1 border-t border-black/[0.08]" />
                  <span className="token-10 text-glass-muted font-mono whitespace-nowrap">
                    {entry.dayKey}
                  </span>
                  <div className="flex-1 border-t border-black/[0.08]" />
                </div>
              )}

              {/* Reply speed annotation (between entries, before the setter reply) */}
              {entry.isReplyTurn && entry.replyMinutes !== null && (
                <div className="flex items-center gap-1 ml-2 my-0.5">
                  <div className="w-px h-3 bg-black/[0.08]" />
                  <span className={`token-10 font-mono ${replySpeedColor(entry.replyMinutes)}`}>
                    {formatDuration(entry.replyMinutes)}
                  </span>
                </div>
              )}

              {/* Timeline entry */}
              <button
                ref={(el) => setEntryRef(entry.messageId, el)}
                onClick={() => onEntryClick(entry.messageId)}
                className={`w-full text-left flex items-baseline justify-between gap-2 px-1.5 py-0.5 rounded transition-colors
                  ${isActive ? "bg-black/[0.05]" : "hover:bg-black/[0.03]"}
                  ${entry.dimmed ? "opacity-40" : ""}
                `}
              >
                <span
                  className={`token-11 truncate ${
                    entry.sender === "lead" ? "text-glass-muted" : "text-ds-accent"
                  }`}
                >
                  {entry.displayName}
                </span>
                <span className="token-10 text-glass-muted font-mono whitespace-nowrap flex-shrink-0">
                  {formatTimeInTZ(entry.time, timezone)}
                </span>
              </button>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};
